  /**
   * BreastHealthIntelligencePage — Phase 1 flagship preventive/educational module.
   *
   * 4 секции (Phase 1):
   *  1. Breast Tissue Biology Dashboard (5-zone 3D-стилизирана анатомия + карти)
   *  3. Breast Metabolic Environment (Breast Metabolic Score 0-100 + 5 sub-env)
   *  6. Intervention Engine (Nutrition / Mind&Body / Activity / Supplements)
   *  8. Breast Ecosystem Map (educational pathway diagram)
   *
   *  ⚠️ Strict educational positioning — NO diagnostic / prognostic / treatment claims.
   *
   * Phase 2 ще добави Sections 2 (Density Intelligence), 4 (Benign Conditions),
   * 7 (Breast Tissue Age). Phase 3 — Molecular Pathway Education (без PAM50).
   */
  import MedModuleNotes from "@/components/meds/MedModuleNotes";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import React, { useCallback, useEffect, useState } from "react";
  import { Link } from "react-router-dom";
  import {
    ArrowRight, Loader2, ShieldAlert, Sparkles, Activity, Brain,
    Apple, Pill, ChevronRight, BookOpen, Lock, ScanLine, Stethoscope, Hourglass, Dna,
    CalendarCheck,
    FlaskConical, ChevronDown, ChevronUp,
    Map, Gauge, FileText, Wand2, Heart, Wine, Utensils, ClipboardList, Users, Bell, Award,
  } from "lucide-react";
  import { motion } from "framer-motion";
  import { useAuth } from "@/App";
  import { useLanguage } from "@/i18n/LanguageContext";
  import { useTheme } from "@/theme/ThemeContext";
  import SEO from "@/components/SEO";
  import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
  import BreastAnatomyVisual from "@/components/breast_intel/BreastAnatomyVisual";
  import EcosystemPathwayMap from "@/components/breast_intel/EcosystemPathwayMap";
  import ComputeIndexCTA from "@/components/ComputeIndexCTA";
  // iter593 — Density-specific imports removed; единственият density UI вече е вътре в BreastHealthHub
  import BenignConditionsSection from "@/components/breast_intel/BenignConditionsSection";
  import BreastTissueAgeSection from "@/components/breast_intel/BreastTissueAgeSection";
  import TimeTravelSlider from "@/components/breast_intel/TimeTravelSlider";
  import MolecularPathwayEducation from "@/components/breast_intel/MolecularPathwayEducation";
  import CytologyZoom from "@/components/breast_intel/CytologyZoom";
  import AIHealthSummary from "@/components/breast_intel/AIHealthSummary";
  import NutritionalFingerprint from "@/components/breast_intel/NutritionalFingerprint";  // iter853
  import BhiSection, {
    markPersonalViewSeen, expandAllSections, collapseAllSections, expandSectionsByTestids,
  } from "@/components/breast_intel/BhiSection";
  import { BHI_TRANSLATIONS } from "@/components/breast_intel/bhiTranslations";
  import { computeSmartPriorityTestids } from "@/utils/bhiSmartPriorities";
  import useBhiSnapshot from "@/hooks/useBhiSnapshot";
  import BhiHero from "@/components/breast_intel/BhiHero";
  import BhiClinicalPdfPreviewModal from "@/components/breast_intel/BhiClinicalPdfPreviewModal";
  import BhiFullDossierExportDialog from "@/components/breast_intel/BhiFullDossierExportDialog";
  import BhiDashboardSidebar, { BhiDashboardMobileDrawer } from "@/components/breast_intel/BhiDashboardSidebar";  // iter1032+34 — two-column view + mobile drawer
  import BhiPriorityActionCard from "@/components/breast_intel/BhiPriorityActionCard";  // iter1035 — feature #1
  import BhiReadingLevelToggle from "@/components/breast_intel/BhiReadingLevelToggle";  // iter1035 — feature #3
  import BhiShareDialog from "@/components/breast_intel/BhiShareDialog";  // iter1040 — viral share
  import BhiPathwayAdherencePanel from "@/components/breast_intel/BhiPathwayAdherencePanel";  // iter1037 — feature #4
  import DataLineagePopover from "@/components/DataLineagePopover";  // iter1037 — feature #17
  import { BhiReadingLevelProvider, PlainText } from "@/contexts/BhiReadingLevelContext";           // iter1035
  import { LayoutDashboard as LayoutIcon } from "lucide-react";  // iter1032 — view toggle icon
  import BhiPhaseHeader from "@/components/breast_intel/BhiPhaseHeader";
  import FusionAuditPanel from "@/components/breast_intel/FusionAuditPanel";
  import ZoneDetails from "@/components/breast_intel/ZoneDetails";
  import ScoreRing from "@/components/breast_intel/ScoreRing";
  import StatTile from "@/components/breast_intel/StatTile";
  import SubEnvCard from "@/components/breast_intel/SubEnvCard";  import BhiQuickNav from "@/components/breast_intel/BhiQuickNav";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import BreastPainModule from "@/components/breast/BreastPainModule";
  import BhiComplianceBadges from "@/components/breast_intel/BhiComplianceBadges";  // iter1026
  import QuickLogHabitsCard from "@/components/breast_intel/QuickLogHabitsCard";  // iter933
  // iter878 — BreastCancerRiskPanel import removed (composite risk model deleted)
  // iter933 — BCWeeklyMenuCard removed (duplicate of BCSubtypePanel menu-targeting).
  // import BCWeeklyMenuCard from "@/components/BCWeeklyMenuCard";
  import HereditaryRiskSection from "@/components/breast_intel/HereditaryRiskSection";  // iter625
  import ModifiableFactorsPanel from "@/components/breast_intel/ModifiableFactorsPanel";  // iter880 — Google Health-backed modifiable factors
  import PreventionPathSection from "@/components/breast_intel/PreventionPathSection";  // iter979 — Твоят път към превенция
  import ReferenceContextSection from "@/components/breast_intel/ReferenceContextSection";  // iter981 — Non-modifiable ref context
  // iter990 — PathwayScoresPanel + BdiCrossLinkPanel unified into
  // PathwayExplorer + BdiGenoLab inside the "🧬 Understand" Geno Lab
  // section. Imports removed to keep the bundle lean.
  // iter879 — CompositeBhiRisk import removed (composite Gail-family model deleted; DKG stands alone)
  import BdiGenoLab from "@/components/breast_intel/BdiGenoLab";  // iter650 — BDI Geno Lab
  import ConditionChipsRow from "@/components/breast_intel/ConditionChipsRow";  // iter820 — bidirectional chip ↔ card hover
  import PathwayExplorer from "@/components/breast_intel/PathwayExplorer";  // iter821 — Pathway → Diseases → Foods explorer
  import TwinDashboardCard from "@/components/breast_intel/TwinDashboardCard";  // iter830 — 18 twin dysregulation gauge
  import SavedPathwayMenus from "@/components/breast_intel/SavedPathwayMenus";  // iter824 — Compliance for saved plans
  import BhiSystemicFactorsCard from "@/components/breast_intel/BhiSystemicFactorsCard";  // iter1114.b — methylation + adrenal
  import ModuleRatingButton from "@/components/ModuleRatingButton";  // iter1114.b — rate-per-feature
  import NutritionCoachPanel from "@/components/breast_intel/NutritionCoachPanel";  // iter825 — AI weekly coach
  import CoachJournalPanel from "@/components/breast_intel/CoachJournalPanel";  // iter826 — Longitudinal Coach history
  import { BdiHighlightProvider } from "@/utils/bdiHighlight";
  import BreastTissueRestorationPlan from "@/components/breast_intel/BreastTissueRestorationPlan";  // iter652 — BTRE
  import BreastPreventionProgram from "@/components/breast_intel/BreastPreventionProgram";  // iter653 — BPP
  import BreastHealthCoach from "@/components/breast_intel/BreastHealthCoach";  // iter654 — BHC
  import EvidenceBadge from "@/components/breast_intel/EvidenceBadge";  // iter655 — evidence/confidence
  // iter1041 — SmartAnchorNav import removed (right-rail phase ribbon retired).
  import MyDataHub from "@/components/breast_intel/MyDataHub";  // iter657 — patient data entry hub
  import MyDataLinkCard from "@/components/common/MyDataLinkCard";  // iter1010 — един център за данните
  import ScreeningPlanCard from "@/components/breast_intel/ScreeningPlanCard";  // iter1014 — план за скрининг
  import { useBhiLayout, BhiSectionOrderPanel, BhiOrderContext, useBhiOrderStyle } from "@/components/breast_intel/BhiLayoutOrder";  // iter1016 — ред на модулите
  import { useViewPrefs } from "@/lib/useViewPrefs";  // iter1073
  import { ViewCustomizeBar, HideableSection, ViewPrefsContext } from "@/components/shared/ViewCustomizeBar";  // iter1073
  import DocumentHistoryPanel from "@/components/breast_intel/DocumentHistoryPanel";  // iter875
  import KeggActivityPanel from "@/components/breast_intel/KeggActivityPanel";  // iter884
  import BhiSankeyCard from "@/components/breast_intel/BhiSankeyCard";  // iter999 — Sankey поток
  import BCSubtypePanel from "@/components/breast_intel/BCSubtypePanel";  // iter922 — molecular subtype + BC-friendly targeting
  import LivingMetabolismCard from "@/components/breast_intel/LivingMetabolismCard";  // iter926 — living metabolism loop
  import SpecialistLabProfile from "@/components/breast_intel/SpecialistLabProfile";  // iter659 — DUTCH/DNA profile
  import BhiNudgeFeed from "@/components/breast_intel/BhiNudgeFeed";  // iter661 — cross-module follow-up actions
  import BhiPatientJourney from "@/components/breast_intel/BhiPatientJourney";  // iter663 — 6-stage patient journey
  import HormoneTrendChart from "@/components/breast_intel/HormoneTrendChart";  // iter663 — hormone time-series
  import BhiOnboardingWizard from "@/components/breast_intel/BhiOnboardingWizard";  // iter667 — 5-min onboarding
  import BhiPlateBridgeCard from "@/components/breast_intel/BhiPlateBridgeCard";  // iter739 — BHI → Plate suggestions
  import BhiMetabolicDeltaCard from "@/components/breast_intel/BhiMetabolicDeltaCard";  // iter1041 — 30-day delta viz

// iter1041 — Stable empty fallback refs. Passing inline `{}` / `[]` would
// give a NEW reference on every render, retriggering downstream useEffect /
// useCallback that include these in their deps (root cause of the flicker
// in BTRE / Prevention Program / Health Coach sections).
const EMPTY_OBJ = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);
  import BhiSnapshotSaveCard from "@/components/breast_intel/BhiSnapshotSaveCard";  // iter740 — save/list snapshots
  import BhiGettingStartedModal from "@/components/breast_intel/BhiGettingStartedGuide";  // iter742 — 30-day orientation modal
  import BhiAuditModal from "@/components/breast_intel/BhiAuditModal";  // iter750 — Full signal audit
  import BhiReferralWidget from "@/components/breast_intel/BhiReferralWidget";  // iter668 — share with friend
  import ManualBiRadsCard from "@/components/breast_intel/ManualBiRadsCard";  // iter698 — manual BI-RADS only (no scan)
  import TwinAttributionBadge from "@/components/breast_intel/TwinAttributionBadge";  // iter698 — twin source chips
  import MethodologyInfo from "@/components/MethodologyInfo";  // iter701 — methodology popup
  import GeneticNutritionSection from "@/components/breast_intel/GeneticNutritionSection";  // iter705 — genetic profile
  import RawDnaUploadCard from "@/components/breast_intel/RawDnaUploadCard";  // iter706 — raw 23andMe/Ancestry upload
  import ConditionInfluencePrompt from "@/components/breast_intel/ConditionInfluencePrompt";  // iter760 — BHI ↔ Twins prompt
  import WhatIfSimulator from "@/components/breast_intel/WhatIfSimulator";  // iter770 — Educational predictive simulator
  import RecoveryPathSimulator from "@/components/breast_intel/RecoveryPathSimulator";  // iter780 — Diagnosis→recovery simulator
  import { useHealthEvent } from "@/utils/healthEvents";  // iter760 — cross-module live refresh

  // iter847 — PersonalViewToggle moved into BhiHero (self-contained).

  const TX = BHI_TRANSLATIONS;


  const EVIDENCE_COLORS = {
    A: { bg: "bg-emerald-100", text: "text-emerald-700", dark: "bg-emerald-500/20 text-emerald-300" },
    B: { bg: "bg-amber-100",   text: "text-amber-700",   dark: "bg-amber-500/20 text-amber-300"   },
    C: { bg: "bg-slate-100",   text: "text-slate-700",   dark: "bg-slate-500/20 text-slate-300"   },
  };

  const BAND_PILL = {
    green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
    red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  };

  export default function BreastHealthIntelligencePage() {
    const { user, API, loading: authLoading } = useAuth();
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const lang = language === "en" ? "en" : language === "es" ? "es" : "bg";
    const t = TX[lang];

    // iter845 — Snapshot fetching + live-refetch on conditions-changed
    // now lives in a dedicated hook.
    const { data, loading, err, refresh: fetchSnapshot, refreshKey, bumpRefreshKey } = useBhiSnapshot();
    const [selectedZone, setSelectedZone] = useState("epithelium");
    const [monthsAhead, setMonthsAhead] = useState(0);
    const [cytologyZone, setCytologyZone] = useState(null);
    const [hereditaryResult, setHereditaryResult] = useState(null);
    // iter1016 — ред на модулите (админът може да го променя)
    const { layout, orderMap, setLayout, loaded: layoutLoaded } = useBhiLayout();
    // iter1073 — потребителят сам маха секции от този екран
    const bhiPrefs = useViewPrefs("bhi");
    const ord = (id) => (orderMap ? { order: orderMap[id] ?? 9990 } : undefined);
    const [sharedCtx, setSharedCtx] = useState(null);
    const [gsOpen, setGsOpen] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const [clinicalPdfOpen, setClinicalPdfOpen] = useState(false);

    // iter1043 — Tier'd Disclosure (Essential vs Advanced) to solve cognitive
    // overload. Essential mode renders only 5 core sections; Advanced mode
    // (default for returning users) renders all 29 sections.
    const [viewTier, setViewTier] = useState(() => {
      try {
        const stored = localStorage.getItem("bhi_view_tier");
        if (stored === "essential" || stored === "advanced") return stored;
      } catch { /* private mode */ }
      return "essential";  // new users start focused
    });
    const setPersistentTier = (tier) => {
      setViewTier(tier);
      try { localStorage.setItem("bhi_view_tier", tier); } catch { /* noop */ }
    };

    // iter1032 — Two-column dashboard view (opt-out).
    // Default: `dashboard` on ≥1280px screens, `classic` otherwise.
    // User preference persists in localStorage under `bhi_view`.
    const [viewMode, setViewMode] = useState(() => {
      try {
        const stored = localStorage.getItem("bhi_view");
        if (stored === "dashboard" || stored === "classic") return stored;
      } catch { /* private mode */ }
      // Default: dashboard for wide screens
      if (typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches) {
        return "dashboard";
      }
      return "classic";
    });
    const [dashActiveId, setDashActiveId] = useState("patient-journey");
    const setPersistentView = (next) => {
      setViewMode(next);
      try { localStorage.setItem("bhi_view", next); } catch { /* private mode */ }
    };
    const handleDashNavigate = (item) => {
      setDashActiveId(item.id);
      // iter1043 — Auto-upgrade to Advanced tier when navigating to a
      // section that's hidden in Essential mode. Otherwise clicking would
      // do nothing (target testid absent from DOM).
      const essentialSet = new Set([
        "section-metabolic", "section-hereditary", "section-tissue-plan",
        "section-interventions", "section-ai-summary",
        "bhi-daily-companion",  // always-visible (not filtered)
      ]);
      if (viewTier === "essential" && !essentialSet.has(item.testid)) {
        setPersistentTier("advanced");
      }
      // iter1033 — Fix 2: broadcast expand event so BhiSection wrapping the
      // target testid opens automatically.
      expandSectionsByTestids([item.testid], { scroll: false });
      // iter1036 — Use scrollIntoView with a small delay for expansion animation.
      // BhiSection has `scroll-margin-top: 96px` so the sticky top-nav is respected.
      // Previous manual `scrollTo(y - 90) + clamp` caused late-in-page targets to
      // land at the very bottom of the document.
      // iter1043 — allow ~350ms for the Advanced-tier upgrade re-render.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${item.testid}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    };

    // iter1033 — Fix 3: scroll-spy. Update `dashActiveId` as user scrolls
    // through sections. Uses IntersectionObserver on all `[data-testid^="section-"]`.
    // iter1038 — also runs in Classic mode so mobile drawer highlights work.
    useEffect(() => {
      // iter1034 — also observe `bhi-daily-companion` (not a section-* but a
      // sidebar-linkable Quick Log widget).
      const nodes = [
        ...document.querySelectorAll('[data-testid^="section-"]'),
        ...document.querySelectorAll('[data-testid="bhi-daily-companion"]'),
      ];
      if (!nodes.length) return;
      // Map testid → sidebar item id
      // NB: use plain object (not `new Map`) because this file imports `Map`
      // from lucide-react (icon), which shadows the built-in.
      const testidToItemId = {
        "section-patient-journey":       "patient-journey",
        "bhi-daily-companion":           "daily-habits",
        "section-ai-summary":            "ai-summary",
        "section-nutritional-fingerprint":"nutritional-fp",
        "section-top-stats":             "top-stats",
        "section-nudge-feed":            "nudge-feed",
        "section-plate-bridge":          "plate-bridge",
        "section-my-data":               "my-data",
        "section-doc-history":           "doc-history",
        "section-auto-diagnosis":        "auto-diagnosis",
        "section-bc-subtype":            "bc-subtype",
        "section-speclab":               "speclab",
        "section-hereditary":            "hereditary",
        "section-prevention-path":       "prevention-path",
        "section-reference-context":     "reference-context",
        "section-biology-observe":       "biology-observe",
        "section-density":               "density",
        "section-benign":                "benign",
        "section-tissue-age":            "tissue-age",
        "section-metabolic":             "metabolic",
        "section-molecular":             "molecular",
        "section-geno-lab":              "geno-lab",
        "section-living-metabolism":     "living-metabolism",
        "section-kegg-activity":         "kegg-activity",
        "section-tissue-plan":           "tissue-plan",
        "section-prevention-program":    "prevention-program",
        "section-simulators":            "simulators",
        "section-coach":                 "coach",
        "section-interventions":         "interventions",
        "section-ecosystem":             "ecosystem",
      };
      const io = new IntersectionObserver(
        (entries) => {
          // Pick the top-most section whose top is above the viewport middle.
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length) {
            const testid = visible[0].target.getAttribute("data-testid");
            const itemId = testidToItemId[testid];
            if (itemId) setDashActiveId(itemId);
          }
        },
        { rootMargin: "-100px 0px -60% 0px", threshold: 0.01 }
      );
      nodes.forEach((n) => io.observe(n));
      return () => io.disconnect();
    }, [viewMode, data, loading]);

    // Legacy alias kept for downstream consumers that read speclabRefreshKey
    const speclabRefreshKey = refreshKey;

    // Auth gate
    if (!authLoading && !user) {
      return (
        <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}>
          <SEO title={t.title} description={t.description} path="/health-intelligence/breast" />
          <SubpageNav />
          <div className="max-w-3xl mx-auto px-4 py-12 text-center">
            <Lock className="w-12 h-12 mx-auto opacity-50 mb-4" />
            <h1 className="text-2xl font-bold mb-2">{t.loginCta}</h1>
            <p className="opacity-70 mb-6">{t.loginCtaText}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/login" className="px-5 py-2.5 rounded-lg bg-pink-600 text-white font-semibold" data-testid="bhi-login-btn">
                {t.loginBtn}
              </Link>
              <Link to="/register" className="px-5 py-2.5 rounded-lg bg-gray-900 text-white font-semibold" data-testid="bhi-register-btn">
                {t.registerBtn}
              </Link>
            </div>
          </div>
          <SubpageFooter />
        </div>
      );
    }

    const cardCls = `rounded-2xl border ${
      isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
    }`;

    return (
      <BhiReadingLevelProvider>
      <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}>
        <SEO title={t.title} description={t.description} path="/health-intelligence/breast" />
        <SubpageNav />

        <div className={`${viewMode === "dashboard" ? "max-w-[1600px]" : "max-w-7xl"} mx-auto px-4 sm:px-6 py-6 sm:py-10`}>
          <SubpageBackLink to="/dashboard/health-intelligence" label={t.back} />

          {/* Quick nav — primary platform destinations */}
          <BhiQuickNav isDark={isDark} lang={lang} activeRoute="/health-intelligence/breast" />

          {/* iter1026 — GDPR + EU AI Act compliance badges */}
          <BhiComplianceBadges />

          {/* iter1022 — профилът „Моите данни“ захранва и BHI */}
          <ProfileContextStrip domain="breast" isDark={isDark} className="mb-4" />

          {/* Hero — extracted to BhiHero (iter846+iter847) */}
          <BhiHero
            isDark={isDark}
            lang={lang}
            t={t}
            user={user}
            onGettingStarted={() => setGsOpen(true)}
            onAudit={() => setAuditOpen(true)}
            onOpenClinicalPdf={() => setClinicalPdfOpen(true)}
            smartExpandCount={data ? computeSmartPriorityTestids({ snapshot: data, hereditary: hereditaryResult }).length : 0}
            onSmartExpand={() => {
              const ids = computeSmartPriorityTestids({ snapshot: data, hereditary: hereditaryResult });
              expandSectionsByTestids(ids);
            }}
          />
          <BhiClinicalPdfPreviewModal
            open={clinicalPdfOpen}
            onClose={() => setClinicalPdfOpen(false)}
          />

          {/* iter1041 — SmartAnchorNav (right-rail phase ribbon) removed by user request.
              Navigation is fully covered by BhiDashboardSidebar (desktop) +
              BhiDashboardMobileDrawer (mobile). */}

          {/* iter943 — Full BHI Dossier PDF export (all 6 modules, bilingual) */}
          <div className="flex justify-end -mt-2 mb-3 gap-2 items-center flex-wrap" data-testid="bhi-dossier-export-toolbar">
            {/* iter1043 — Essential vs Advanced tier toggle (cognitive overload fix) */}
            <div className={`inline-flex rounded-lg border overflow-hidden ${isDark ? "border-slate-700" : "border-slate-300"}`}
                 data-testid="bhi-tier-toggle">
              <button
                type="button"
                onClick={() => setPersistentTier("essential")}
                data-testid="bhi-tier-toggle-essential"
                className={`px-3 py-1.5 text-xs font-semibold transition ${
                  viewTier === "essential"
                    ? "bg-emerald-600 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
                title="Фокусиран изглед: само 5-те основни секции"
              >
                🌱 Essential
              </button>
              <button
                type="button"
                onClick={() => setPersistentTier("advanced")}
                data-testid="bhi-tier-toggle-advanced"
                className={`px-3 py-1.5 text-xs font-semibold transition border-l ${isDark ? "border-slate-700" : "border-slate-300"} ${
                  viewTier === "advanced"
                    ? "bg-purple-600 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
                title="Пълен изглед: всички 29 секции"
              >
                🔬 Advanced
              </button>
            </div>

            {/* iter1035 — Reading level toggle (Clinical vs Plain / Explain-Like-I'm-5) */}
            <BhiReadingLevelToggle isDark={isDark} />

            {/* iter1040 — Share dialog (viral / social loop) */}
            <BhiShareDialog isDark={isDark} />

            {/* iter1032 — View toggle (Dashboard vs Classic). Dashboard is default on ≥1280px screens. */}
            <div className={`inline-flex rounded-lg border overflow-hidden ${isDark ? "border-slate-700" : "border-slate-300"}`}
                 data-testid="bhi-view-toggle">
              <button
                type="button"
                onClick={() => setPersistentView("dashboard")}
                data-testid="bhi-view-toggle-dashboard"
                className={`px-3 py-1.5 text-xs font-semibold transition ${
                  viewMode === "dashboard"
                    ? "bg-emerald-600 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
                title="Two-column dashboard изглед с sticky sidebar"
              >
                <LayoutIcon className="w-3.5 h-3.5 inline mr-1" />
                Dashboard
              </button>
              <button
                type="button"
                onClick={() => setPersistentView("classic")}
                data-testid="bhi-view-toggle-classic"
                className={`px-3 py-1.5 text-xs font-semibold transition border-l ${isDark ? "border-slate-700" : "border-slate-300"} ${
                  viewMode === "classic"
                    ? "bg-slate-700 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
                title="Класически вертикален изглед"
              >
                Classic
              </button>
            </div>
            <BhiFullDossierExportDialog />
          </div>

          {/* Disclaimer banner — prominent */}
          {/* iter1036 — "Важно / Научен и медицински статус" disclaimer banner
              премахнат. Съобщението е излишен пенкилер в началото — вече имаме
              compliance chips горе (GDPR / EU AI Act / Educational) и
              потребителят вижда същия текст в footer-а. */}

          {/* iter933-Batch3-C1 — Daily Companion (Живият метаболизъм quick-tap).
              Positioned RIGHT AFTER the hero+disclaimer so it's the first
              interactive element the patient sees. Each tap immediately
              boosts the BHI environment scores rendered below and ripples
              through the KEGG pathway vault → ecosystem map (via the
              `habits-changed` health event). See BREAST HEALTH DESIGN NOTE:
              this card is the "bridge" between BDI/BPP/BTRE recommendations
              (which describe WHAT to do) and BHI environment scores (which
              show HOW you're doing over time). */}
          {!loading && data && (
            <div className="mb-6" data-testid="bhi-daily-companion">
              <QuickLogHabitsCard />
            </div>
          )}

          {!loading && data && (
            <div className="mb-6" data-testid="bhi-breast-pain-wrap">
              <BreastPainModule isDark={isDark} />
            </div>
          )}

          {/* iter1041 — Living Metabolism → BHI delta viz. Renders "30 days ago vs today"
              per-component bars. Renders even without snapshots (empty-state). */}
          {!loading && data && (
            <div className="mb-6" data-testid="bhi-metabolic-delta-wrap">
              <BhiMetabolicDeltaCard isDark={isDark} />
            </div>
          )}

          {/* Loading / error / empty */}
          {loading && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-50" />
              <p className="mt-3 text-sm opacity-70">{lang === "en" ? "Loading…" : lang === "es" ? "Cargando…" : "Зареждане…"}</p>
            </div>
          )}
          {err && (
            <div className="p-4 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="bhi-error">
              {err}
            </div>
          )}

          {data && !loading && (
            <div className={viewMode === "dashboard" ? "flex gap-6 items-start" : ""}>
              {/* iter1032 — Sticky sidebar (only in Dashboard mode, only ≥ lg breakpoint) */}
              {viewMode === "dashboard" && (
                <BhiDashboardSidebar
                  isDark={isDark}
                  activeId={dashActiveId}
                  onNavigate={handleDashNavigate}
                  orderMap={orderMap}
                />
              )}

              {/* iter1034 — Mobile drawer (floating hamburger + slide-in). Renders on <lg only.
                  iter1038 — also render in Classic mode so mobile users always get navigation. */}
              <BhiDashboardMobileDrawer
                isDark={isDark}
                activeId={dashActiveId}
                onNavigate={handleDashNavigate}
                orderMap={orderMap}
              />


              {/* Main content column */}
              <BhiOrderContext.Provider value={orderMap}>
              <ViewPrefsContext.Provider value={bhiPrefs}>
              <div className={`${viewMode === "dashboard" ? "flex-1 min-w-0" : ""} ${
                orderMap ? "flex flex-col" : ""}`}
                style={layoutLoaded ? undefined : { visibility: "hidden" }}
                aria-busy={!layoutLoaded}>
              {/* iter1073 — „Подредете изгледа": всяка секция може да се скрие */}
              <div style={orderMap ? { order: -1001 } : undefined}>
                <ViewCustomizeBar
                  isDark={isDark}
                  editMode={bhiPrefs.editMode}
                  setEditMode={bhiPrefs.setEditMode}
                  hiddenCount={bhiPrefs.hidden.length}
                  reset={bhiPrefs.reset}
                  testid="bhi-view-customize"
                />
              </div>
              {/* iter1016 — админ панел за подреждане на модулите */}
              {user?.role === "admin" && (
                <BhiSectionOrderPanel isDark={isDark} layout={layout} setLayout={setLayout}
                  style={orderMap ? { order: -1000 } : undefined} />
              )}
              {/* iter1035 — Priority Action Card (AI-selected top action for today) */}
              <div style={ord("pre-priority-action")}>
                <BhiPriorityActionCard isDark={isDark} />
              </div>

              {/* iter1037 — Feature #4 · Pathway Adherence Panel */}
              <div className="mb-6" style={ord("pre-adherence")}>
                <BhiPathwayAdherencePanel isDark={isDark} />
              </div>

              {/* Cross-influence prompt: encourage user to add
                  structured conditions since BHI reads them directly. */}
              <div style={ord("pre-condition-prompt")}>
                <ConditionInfluencePrompt isDark={isDark} lang={lang} />
              </div>

              {/* iter663 — Patient Journey (keeps default open — this is the master roadmap)
                  iter1033 — wrap in <div data-testid="section-patient-journey"> so dashboard
                             sidebar can smooth-scroll to it (was previously an unwrapped component). */}
              <div data-testid="section-patient-journey"
                style={{ scrollMarginTop: 96, ...(ord("section-patient-journey") || {}) }}>
                <BhiPatientJourney snapshot={data} lang={lang} />
              </div>

              {/* iter995 — All widgets below are wrapped in Section for
                  consistent collapsible behaviour. Empty ESSENTIAL_TESTIDS
                  means everything opens collapsed; user expands what they
                  need via chevron or the "Разгъни всички" toolbar button. */}

              <Section icon={Brain} title="Обобщение от AI за здравето на гърдата" help="Лично резюме на Вашите резултати, изготвено от AI. Обновява се на всеки 24 часа или при промяна на профила." testid="section-ai-summary">
                <AIHealthSummary isDark={isDark} />
              </Section>

              <Section icon={Utensils} title="Нутриционен отпечатък" help="5-осова радар диаграма на диетичните ти показатели (NIS, DIL, UIS, COL3, PRAL)." testid="section-nutritional-fingerprint">
                <NutritionalFingerprint lang={lang} isDark={isDark} />
              </Section>

              <Section icon={Gauge} title="Ключови показатели" help="Бърз преглед на 4 основни метрики от последните 14 дни." testid="section-top-stats">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  <StatTile testid="stat-meals"  label={`${t.meals} (${t.last_14})`} value={data.signals.meals_last_14d}
                    computeRoute="/dashboard/diary" computeLabel={lang === "bg" ? "Логни ястие" : "Log a meal"} lang={lang} isDark={isDark} />
                  <StatTile testid="stat-nis"    label={t.nis_avg}                   value={data.signals.nis_avg || "—"}
                    computeRoute="/dashboard/diary" computeLabel={lang === "bg" ? "Изчисли NIS" : "Compute NIS"} lang={lang} isDark={isDark}
                    tooltip={lang === "bg"
                      ? "NIS (Nutramilo Insulin Score) v1.1.9 — 0–100 %. Прагове: ≤ 35 % ниско (зелено), 36–55 % умерено (кехлибар), > 55 % високо (червено). Базиран на Insulin Index (Holt 1997). По-нисък NIS = по-стабилна глюкозо-инсулинова реакция."
                      : "NIS (Nutramilo Insulin Score) v1.1.9 — 0–100 %. Thresholds: ≤ 35 % low (green), 36–55 % moderate (amber), > 55 % high (red). Based on Insulin Index (Holt 1997). Lower NIS = more stable glucose-insulin response."} />
                  <StatTile testid="stat-dil"    label={t.dil_avg}                   value={data.signals.dil_avg || "—"}
                    computeRoute="/dashboard/diary" computeLabel={lang === "bg" ? "Изчисли DIL" : "Compute DIL"} lang={lang} isDark={isDark}
                    tooltip={lang === "bg"
                      ? "DIL (Диетичен инсулинов товар) на 100 g. Прагове: ≤ 25 много нисък, 26–45 нисък, 46–65 умерен, > 65 висок. Дневен бюджет: закуска ≤ 29, обяд ≤ 33, вечеря ≤ 19, междинно ≤ 10 (Holt 1997 · Nutramilo v1.1.9)."
                      : "DIL (Dietary Insulin Load) per 100 g. Thresholds: ≤ 25 very low, 26–45 low, 46–65 moderate, > 65 high. Per-meal budget: breakfast ≤ 29, lunch ≤ 33, dinner ≤ 19, snack ≤ 10 (Holt 1997 · Nutramilo v1.1.9)."} />
                  <StatTile testid="stat-bmi"    label={t.bmi}                       value={data.signals.bmi || "—"}
                    computeRoute="/profile" computeLabel={lang === "bg" ? "Попълни BMI" : "Set BMI"} lang={lang} isDark={isDark} />
                </div>
              </Section>

              {/* iter667 — Onboarding Wizard (shows only for new users with no uploads) */}
              <div style={ord("pre-onboarding-wizard")}>
                <BhiOnboardingWizard
                onStartUpload={() => {
                  const el = document.querySelector('[data-testid="my-data-labs"]') || document.querySelector('[data-testid="bhi-patient-journey"]');
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
              </div>

              <Section icon={Gauge} title="Физиологичен мост"
                help="Обем и осмолалитет, електролити, мембранен транспорт и хомеостазни контури — общият език с останалите близнаци."
                testid="section-physiology-bridge">
                <PhysiologyBridgeCard focus="breast" isDark={isDark} />
              </Section>

              <Section icon={FileText} title="Моите данни" help="Качи мамограма, стандартни лаборатории, DUTCH, ДНК тестове." testid="section-my-data">
                <MyDataHub snapshot={data} lang={lang} onSpeclabUploaded={bumpRefreshKey} />
              </Section>

              <Section icon={ClipboardList} title="История на документите" help="Хронология на всички качени епикризи, лаборатории и генетични тестове." testid="section-doc-history">
                <DocumentHistoryPanel lang={lang} refreshKey={speclabRefreshKey} />
              </Section>

              <Section icon={Stethoscope} title="Автоматична диагноза" help="Агрегиран скрининг върху всички твои лаборатории — сигнали за метаболитни, хормонални и възпалителни състояния." testid="section-auto-diagnosis">
                <MyDataLinkCard
                  testid="bhi-autodx-link"
                  title="Автодиагноза от всички твои изследвания"
                  note="Автодиагнозата се изчислява веднъж от общите данни и живее в профила,
                        за да няма два различни списъка с диагнози."
                  cta="Виж автодиагнозата в раздела Моите данни" />
              </Section>

              <Section icon={Dna} title="BC молекулярен подтип" help="Ако си диагностицирана с рак на гърдата — деклариран или предвиден подтип и подходящи хранителни теми." testid="section-bc-subtype">
                <BCSubtypePanel />
              </Section>

              <Section icon={Heart} title="Жив метаболизъм" help="Ежедневни отметки за изпълнение → Vault тегла се обновяват → BHI преизчислява." testid="section-living-metabolism">
                <LivingMetabolismCard />
              </Section>

              <Section icon={Activity} title="KEGG активност" help="Heat-map на активните биологични пътища от твоите приети диагнози и рискови фактори." testid="section-kegg-activity">
                <KeggActivityPanel lang={lang} />
              </Section>

              {/* iter999 — Sankey: фактори → пътища → клинични резултати */}
              <Section icon={Activity} title="Поток на риска (Sankey)" help="Откъде идва BHI натоварването: всеки рисков фактор → сигналните пътища, които активира → клиничния резултат." testid="section-bhi-sankey">
                <BhiSankeyCard />
              </Section>

              <Section icon={Bell} title="Действия за проследяване" help="DUTCH/ДНК препоръчани следващи стъпки на база резултати." testid="section-nudge-feed">
                <BhiNudgeFeed lang={lang} refreshKey={speclabRefreshKey} />
              </Section>

              <Section icon={Apple} title="Мост към чинията" help="Куратирани хранителни предложения на база твоя BHI." testid="section-plate-bridge">
                <BhiPlateBridgeCard isDark={isDark} />
              </Section>

              {/* iter656 — Patient-centric flow: ASSESS → OBSERVE → UNDERSTAND → PLAN → ACT → SUPPORT */}

              {/* ═══════════════════ 🪞 ASSESS ═══════════════════ */}
              <div style={ord("phase-assess")}>
                <PhaseHeader emoji="🪞" title={t.phases.assess} subtitle={t.phases.assess_sub} anchorId="assess" /></div>

              {/* iter625 — SECTION 2A: Наследствен риск (DKG checklist).
                  DKG е клинично валидиран инструмент от Deutsche Krebsgesellschaft —
                  дава конкретни NCCN 2026 препоръки без изчисление на процент риск. */}
              <Section icon={Dna} title="Наследствен риск от рак на гърдата (DKG · NCCN 2026 · AGO 2026)" help="DKG (Deutsche Krebsgesellschaft) въпросник + NCCN/AGO препоръки. Резултатът дава конкретни клинични действия (screening, генетик, chemoprevention)." testid="section-hereditary">
                <HereditaryRiskSection onResultChange={setHereditaryResult} />
              </Section>

              {/* iter1016 — планът за скрининг стои веднага след DKG (по желание на продукта) */}
              <Section icon={CalendarCheck} title="Моят план за скрининг" help="Кога и с какво изследване — мамография, ЯМР, клиничен преглед — според Вашата категория риск, плътност на гърдата и фамилна анамнеза." testid="section-screening-plan">
                <ScreeningPlanCard isDark={isDark} />
              </Section>

              {/* iter994 — Onboarding banner moved to top-of-page (above AI Summary). */}

              {/* iter881 — SECTION 2B hidden per product decision (Google Health integration paused).
                  ModifiableFactorsPanel запазена в codebase; ще бъде заменена от Рискови фактори с KEGG dose-response mapping. */}
              {/* <Section icon={Sparkles} title="Модифицируеми фактори (WCRF/AICR 2018 · WHO 2020)" ...>
                <ModifiableFactorsPanel />
              </Section> */}

              {/* iter979 — SECTION 2C: Твоят път към превенция (25 модифицируеми рискови фактори
                  с KEGG pathways + клинични доказателства). */}
              <Section icon={Sparkles} title="Твоят път към превенция" help={`Избери твоя актуален профил за 26 фактора, върху които можеш да въздействаш. Всеки фактор е свързан с конкретни биологични процеси и клинични данни — натисни бутона за информация за детайли.`} testid="section-prevention-path">
                <PreventionPathSection />
              </Section>

              {/* iter981 — SECTION 2D: Референтен контекст (non-modifiable) */}
              <Section icon={ShieldAlert} title="Данни от твоя профил" help="16 факта, които не се променят с времето (напр. ръст, менархе, фамилна анамнеза), но участват в биологичното изчисление. Част се попълват автоматично от твоя здравен профил." testid="section-reference-context">
                <ReferenceContextSection />
              </Section>

              {/* iter990 — SECTIONS 2E + 2F removed (unified into Geno Lab).
                  Everything the former PathwayScoresPanel showed is now part
                  of PathwayExplorer inside the "🧬 Understand" Geno Lab
                  section, and the former BdiCrossLinkPanel is merged into
                  BdiGenoLab (BHI overlap chip + green driving-factors box
                  per disease card). Single source of truth. */}

              {/* iter879 — CompositeBhiRisk embed removed together with the underlying Gail-family model. */}

              {/* ═══════════════════ 🔬 OBSERVE ═══════════════════ */}
              <div style={ord("phase-observe")}>
                <PhaseHeader emoji="🔬" title={t.phases.observe} subtitle={t.phases.observe_sub} anchorId="observe" /></div>

              {/* Лекарства, добавки и билки от общия списък на пациента */}
              <div className="mb-4" style={ord("observe-med-notes")}>
                <MedModuleNotes module="bhi" cardCls={cardCls} isDark={isDark} />
              </div>

              {/* ── Biology Dashboard (anatomical zones) ─────── */}
              <Section icon={Sparkles} title={t.sections.biology} help={<PlainText clinical={t.biologyHelp} plain={t.biologyHelpPlain} />} testid="section-biology-observe">
                <BiologyDashboardInline data={data} monthsAhead={monthsAhead} setMonthsAhead={setMonthsAhead}
                  selectedZone={selectedZone} setSelectedZone={setSelectedZone}
                  cardCls={cardCls} isDark={isDark} lang={lang} t={t} />
              </Section>

              {/* iter698 — Density: MANUAL BI-RADS only (no mammogram upload, no MONAI/AI).
                  BI-RADS category is stored on the user profile and shown to
                  the BHI AI summary as one of the observation inputs. */}
              <Section
                icon={ScanLine}
                title={t.sections.density}
                help={t.densityHelp}
                testid="section-density"
                methodologyInfo={
                  <MethodologyInfo
                    methodology={{
                      formula: "BI-RADS A/B/C/D — RR от ACR референтни таблици",
                      inputs_used: ["Manual BI-RADS (от радиологичен доклад)", "Optional: percent density", "Възраст"],
                      what_we_do_NOT_use: [
                        "Софтуерно/AI разпознаване на плътност от мамографско изображение",
                        "Volumetric density (Volpara, Quantra)",
                      ],
                      band_thresholds: { "A": "Почти изцяло мазнина", "B": "Разпръснати жлезисти", "C": "Хетерогенно плътна", "D": "Изключително плътна" },
                      documentation: "/app/memory/BDI_METHODOLOGY.md",
                    }}
                    limitations={[
                      "BI-RADS се въвежда РЪЧНО от Вашия радиологичен доклад. Стойността НЕ е софтуерно изчислена.",
                      "RR стойностите са общи популационни асоциации (ACR), НЕ персонална прогноза.",
                      "За цялостен риск използвайте  (IBIS) или BOADICEA в специализиран клиничен преглед.",
                    ]}
                    lang={lang}
                    isDark={isDark}
                    testid="bhi-density"
                  />
                }
              >
                <div className="space-y-4">
                  <ManualBiRadsCard
                    lang={lang}
                    isDark={isDark}
                    onSaved={() => fetchSnapshot()}
                  />
                  {/* iter878 — Risk calculator removed; BHI фокусира се върху модифицируеми сигнали (NIS/DIL, метаболитни маркери, lifestyle). */}
                </div>
              </Section>

              {/* ── Benign Breast Conditions ─────────────────────
                   iter901 — Hidden per user request to avoid patient
                   confusion. Section was informational-only but users
                   sometimes assumed they had listed conditions when
                   no data was present. Component + backend data
                   preserved for future re-enable. */}
              {false && (
                <Section icon={Stethoscope} title={t.sections.benign} help={t.benignHelp} testid="section-benign">
                  <BenignConditionsSection benign={data.benign} cardCls={cardCls} isDark={isDark} lang={lang} />
                </Section>
              )}

              {/* ── Metabolic Environment ──────────────────────── */}
              <Section icon={Activity} title={t.sections.metabolic} help={<PlainText clinical={t.metabolicHelp} plain={t.metabolicHelpPlain} />} testid="section-metabolic">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className={`${cardCls} p-6 flex flex-col items-center`}>
                    <ScoreRing score={data.metabolic.total} band={data.metabolic.band} lang={lang} />
                    <div className="mt-3 text-sm font-semibold flex items-center gap-1">
                      Breast Metabolic Score
                      {/* iter1037 — Feature #17 · Data lineage popover */}
                      <DataLineagePopover
                        testid="data-lineage-bms"
                        glossaryKeys={["hs-crp", "homa-ir", "estradiol", "insulin"]}
                        sources={[
                          ...(data?.metabolic?.components?.insulin?.score != null
                            ? [{ kind: "lab", label: "Insulin/HOMA-IR", date: data?.data_freshness?.hormonal_labs || "—", weight: 0.25 }]
                            : [{ kind: "missing", label: "HOMA-IR / инсулин на гладно" }]),
                          ...(data?.metabolic?.components?.inflammation?.score != null
                            ? [{ kind: "lab", label: "hs-CRP / Ω-3", date: data?.data_freshness?.metabolic_labs || "—", weight: 0.2 }]
                            : [{ kind: "missing", label: "hs-CRP" }]),
                          ...(data?.metabolic?.components?.estrogen?.score != null
                            ? [{ kind: "lab", label: "Estradiol / SHBG", date: data?.data_freshness?.hormonal_labs || "—", weight: 0.25 }]
                            : [{ kind: "missing", label: "Estradiol / SHBG" }]),
                          ...(data?.metabolic?.living_metabolism_boost ? [{ kind: "meal_log", label: "Хранителен дневник (14-дневна средна)", weight: 0.15 }] : []),
                          { kind: "manual", label: "Профил + мерки", date: data?.data_freshness?.profile || "—", weight: 0.15 },
                          { kind: "computed", label: "Обобщение (индекс 0–100)", detail: "Претеглена средна на 5 под-среди" },
                        ]}
                      />
                    </div>
                    <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {t.statusBands[data.metabolic.band]}
                    </div>
                  </div>
                  <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {Object.entries(data.metabolic.components).map(([k, v]) => {
                      // iter893 — surface a "missing labs" hint when the
                      // component is diagnosed via conditions but has no
                      // measurable lab to move the score.
                      const _conds = (data?.conditions) || [];
                      const _hasScore = v.score != null;
                      let _hint = null;
                      const _labs = {
                        inflammation: {
                          triggers: ["celiac", "hashimoto", "rheumatoid_arthritis", "ibd", "psoriasis", "lupus", "chronic_inflammation", "nafld", "endometriosis"],
                          labs_bg: "hs-CRP или Omega-3 индекс",
                          labs_en: "hs-CRP or Omega-3 index",
                        },
                        insulin: {
                          triggers: ["type2_diabetes", "insulin_resistance", "metabolic_syndrome", "pcos", "prediabetes"],
                          labs_bg: "HOMA-IR, глюкоза или инсулин на гладно",
                          labs_en: "HOMA-IR, fasting glucose or insulin",
                        },
                        estrogen: {
                          triggers: ["endometriosis", "pcos"],
                          labs_bg: "DUTCH test за естрогенни метаболити",
                          labs_en: "DUTCH test for estrogen metabolites",
                        },
                      };
                      const _spec = _labs[k];
                      if (_spec && !_hasScore && _conds.some((c) => _spec.triggers.includes(c))) {
                        _hint = lang === "bg"
                          ? `Липсват labs (${_spec.labs_bg}) — качете за точен резултат`
                          : `Missing labs (${_spec.labs_en}) — upload for a precise score`;
                      }
                      return (
                        <SubEnvCard
                          key={k}
                          name={k}
                          score={v.score}
                          band={v.band}
                          confidence={v.confidence}
                          evidenceId={v.evidence_id}
                          lang={lang}
                          speclabDelta={v.speclab_delta}
                          baseScore={v.base_score}
                          missingLabsHint={_hint}
                          livingBoost={v.living_boost}
                          livingOnlyHint={v.living_only_hint}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* iter933 — Global heuristic disclaimer so patient understands
                    that components without direct labs are educational estimates,
                    not measured values (Estrogen without DUTCH, Inflammation
                    without hs-CRP, etc. are heuristic-driven). */}
                <div className={`mt-3 p-2.5 rounded-lg text-[10px] italic leading-relaxed ${
                  isDark ? "bg-amber-500/10 text-amber-200/90" : "bg-amber-50 text-amber-800/90"
                }`} data-testid="metabolic-heuristic-note">
                  📐 Компонентите с пометка „Липсват labs&quot; или без директни лабораторни маркери представят образователна оценка от вашия метаболитен/възпалителен контекст, а НЕ реално измерване. За точна стойност добавете съответните лабораторни изследвания.
                </div>

                {/* iter698 — Twin connection map: показва коя двойка захранва всеки метаболитен компонент */}
                {/* iter932 — Hidden on mobile (cognitive-load reduction).
                    Power-user info; not actionable for patient on-the-go. */}
                {data.twin_attribution?.metabolic && (
                  <div className={`${cardCls} p-4 mt-4 hidden md:block`} data-testid="metabolic-twin-attribution">
                    <div className="text-xs font-semibold opacity-70 mb-3 uppercase tracking-wider">
                      {lang === "bg"
                        ? "Свързаност с други Дигитални Близнаци"
                        : lang === "es"
                        ? "Conexión con otros Gemelos Digitales"
                        : "Connection to other Digital Twins"}
                    </div>
                    <div className="space-y-2.5">
                      {Object.entries(data.twin_attribution.metabolic).map(([compKey, attr]) => {
                        const compLabel = {
                          insulin:      lang === "bg" ? "Инсулин" : lang === "es" ? "Insulina" : "Insulin",
                          igf1:         "IGF-1",
                          inflammation: lang === "bg" ? "Възпаление" : lang === "es" ? "Inflamación" : "Inflammation",
                          estrogen:     lang === "bg" ? "Естроген" : lang === "es" ? "Estrógeno" : "Estrogen",
                          adiposity:    lang === "bg" ? "Адипозитет" : lang === "es" ? "Adiposidad" : "Adiposity",
                        }[compKey] || compKey;
                        return (
                          <div
                            key={compKey}
                            className="flex flex-wrap items-center gap-2 pb-2 border-b border-pink-500/10 last:border-b-0"
                            data-testid={`metabolic-twin-${compKey}`}
                          >
                            <span className="text-xs font-semibold min-w-[90px]">{compLabel}</span>
                            <TwinAttributionBadge
                              twins={attr.twins}
                              registry={data.twin_attribution.twin_registry}
                              markers={attr.markers}
                              size="xs"
                              lang={lang}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] opacity-50 mt-3 italic">
                      {data.twin_attribution.note_bg}
                    </p>
                  </div>
                )}

                {data.metabolic.speclab_fusion?.applied && data.metabolic.speclab_fusion.modifiers?.length > 0 && (
                  <div className="hidden md:block">
                    <FusionAuditPanel fusion={data.metabolic.speclab_fusion} lang={lang} isDark={isDark} />
                  </div>
                )}

                {/* iter931 — Living Metabolism adherence → BHI env boost summary */}
                {data.metabolic.living_metabolism?.has_signal && (
                  <div
                    className={`${cardCls} p-4 mt-4`}
                    data-testid="metabolic-living-metabolism-panel"
                  >
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🌱</span>
                        <div className="text-sm font-bold">
                          {lang === "bg"
                            ? "Живият метаболизъм — 14-дневна адхеренция"
                            : "Living Metabolism — 14-day adherence"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] opacity-80">
                        <span data-testid="lm-executed-count">
                          ✅ {data.metabolic.living_metabolism.executed_14d}
                        </span>
                        <span data-testid="lm-skipped-count">
                          ⏭️ {data.metabolic.living_metabolism.skipped_14d}
                        </span>
                        {data.metabolic.total_before_lm != null && (
                          <span className="font-mono">
                            {data.metabolic.total_before_lm} →{" "}
                            <b className={data.metabolic.total > data.metabolic.total_before_lm ? "text-emerald-500" : ""}>
                              {data.metabolic.total}
                            </b>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                      {["insulin","igf1","inflammation","estrogen","adiposity"].map((axis) => {
                        const boost = data.metabolic.living_metabolism[axis] || 0;
                        const positive = boost > 0;
                        const label = { insulin:"Инсулин", igf1:"IGF-1", inflammation:"Възпаление", estrogen:"Естроген", adiposity:"Мастност" }[axis] || axis;
                        return (
                          <div
                            key={axis}
                            className={`p-2 rounded-lg text-center ${
                              Math.abs(boost) < 0.5
                                ? (isDark ? "bg-white/[0.03]" : "bg-gray-50")
                                : positive
                                  ? (isDark ? "bg-fuchsia-500/15 text-fuchsia-200" : "bg-fuchsia-50 text-fuchsia-800")
                                  : (isDark ? "bg-rose-500/15 text-rose-200" : "bg-rose-50 text-rose-800")
                            }`}
                            data-testid={`lm-axis-${axis}`}
                          >
                            <div className="text-[9px] opacity-70">{label}</div>
                            <div className="text-sm font-bold font-mono">
                              {Math.abs(boost) < 0.5 ? "—" : `${positive ? "+" : ""}${boost}`}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {data.metabolic.living_metabolism.top_env_bg && (
                      <div className={`mt-2 text-[10px] italic ${isDark ? "text-fuchsia-300/70" : "text-fuchsia-700/70"}`}>
                        {lang === "bg"
                          ? `Ежедневните Ви навици най-силно тласкат: ${data.metabolic.living_metabolism.top_env_bg}`
                          : `Your daily habits mostly boost: ${data.metabolic.living_metabolism.top_env_bg}`}
                      </div>
                    )}
                    <div className="mt-1 text-[9px] opacity-60 italic">
                      {lang === "bg"
                        ? "Всяко изпълнено действие дава малка тежест към съответната метаболитна ос. Не замества реалните лабораторни данни, а показва посоката на промяна между анализите."
                        : "Each executed habit adds a small nudge to its axis. Does not replace lab data — shows lifestyle trajectory between analyses."}
                    </div>
                  </div>
                )}
              </Section>

              {/* iter659 — Specialist Lab Profile (DUTCH detox + DNA modulators) */}
              <Section icon={FlaskConical} title={t.sections.speclab} help={lang === "bg" ? "Извлечени маркери от Вашите DUTCH и ДНК тестове — Phase II конюгация, метилация и генетични модулатори" : lang === "es" ? "Marcadores extraídos de sus tests DUTCH y ADN" : "Markers extracted from your DUTCH and DNA tests"} testid="section-speclab">
                <SpecialistLabProfile lang={lang} refreshKey={speclabRefreshKey} />
                {/* iter663 — Hormone trend chart (serum hormones over time) */}
                <div className="mt-4">
                  <HormoneTrendChart refreshKey={speclabRefreshKey} />
                </div>
              </Section>

              {/* ── Tissue Age ─────────────────────────────────── */}
              <Section icon={Hourglass} title={t.sections.tissue_age} help={<PlainText clinical={t.tissueAgeHelp} plain={t.tissueAgeHelpPlain} />} testid="section-tissue-age">
                <BreastTissueAgeSection tissueAge={data.tissue_age} cardCls={cardCls} isDark={isDark} lang={lang} />
              </Section>

              {/* ═══════════════════ 🧬 UNDERSTAND ═══════════════════ */}
              <div style={ord("phase-understand")}>
                <PhaseHeader emoji="🧬" title={t.phases.understand} subtitle={t.phases.understand_sub} anchorId="understand" /></div>

              {/* ── Molecular Pathway Education ────────────────── */}
              <Section
                icon={Dna}
                title={t.sections.molecular}
                help={t.molecularHelp}
                testid="section-molecular"
                methodologyInfo={
                  <MethodologyInfo
                    methodology={{
                      formula: "activation_i = clamp(burden_i + Σ adverse_factors_i − Σ protective_factors_i, 5, 95)",
                      inputs_used: ["BMI", "HOMA-IR", "hsCRP", "Vitamin D", "Smoking", "Alcohol", "Sleep hours", "Exercise min/wk", "Omega-3 index", "NIS/DIL"],
                      what_we_do_NOT_use: [
                        "Преки молекулярни/протеомични измервания",
                        "Genomic експресия (RNA-seq, microarray)",
                        "Клетъчни биопсии или хистология",
                      ],
                      documentation: "/app/memory/BDI_METHODOLOGY.md",
                    }}
                    limitations={[
                      "Седемте пътя са ОБРАЗОВАТЕЛНО представяне базирано на lifestyle factors — не са молекулярно измерени.",
                      "Gating logic: ако <50% от ключовите сигнали липсват, показваме 'Недостатъчно данни' вместо подвеждащ скор.",
                      "За реални молекулярни измервания са нужни специализирани лабораторни тестове (DUTCH, RNA панели).",
                    ]}
                    lang={lang}
                    isDark={isDark}
                    testid="bhi-molecular"
                  />
                }
              >
                <MolecularPathwayEducation molecular={data.molecular_pathways} cardCls={cardCls} isDark={isDark} lang={lang} />
              </Section>

              {/* iter705 — Genetic Nutrition Modulation (variant-driven nutrition levers) */}
              <div style={ord("understand-genetic-nutrition")}>
                <GeneticNutritionSection lang={lang} />
              </div>

              {/* iter706 — Raw 23andMe / Ancestry upload (Phase 3, educational whitelist only) */}
              <div style={ord("understand-raw-dna")}>
                <RawDnaUploadCard
                lang={lang}
                onUploaded={() => { try { window.dispatchEvent(new Event("bhi:dna:refresh")); } catch { /* noop */ } }}
              />
              </div>

              {/* iter650 — BDI: 23 disease cards, AI enrichment, 3-month trends */}
              <Section icon={Dna} title={t.sections.geno_lab} help={null} testid="section-geno-lab">
                <BdiHighlightProvider>
                  <ConditionChipsRow isDark={isDark} />
                  <PathwayExplorer isDark={isDark} />
                  <TwinDashboardCard isDark={isDark} />
                  <NutritionCoachPanel isDark={isDark} />
                  <CoachJournalPanel isDark={isDark} />
                  <SavedPathwayMenus isDark={isDark} />
                  <BdiGenoLab snapshot={data} lang={lang} />
                </BdiHighlightProvider>
              </Section>

              {/* ═══════════════════ 🌿 PLAN ═══════════════════ */}
              <div style={ord("phase-plan")}>
                <PhaseHeader emoji="🌿" title={t.phases.plan} subtitle={t.phases.plan_sub} anchorId="plan" /></div>

              {/* iter652 — BTRE: Breast Tissue Restoration Plan (7-step) */}
              <Section icon={Dna} title={t.sections.tissue_plan} help={null} testid="section-tissue-plan">
                <BreastTissueRestorationPlan
                  snapshot={data}
                  clinical={data?.clinical || EMPTY_OBJ}
                  labs={data?.labs || EMPTY_OBJ}
                  conditions={data?.conditions || EMPTY_ARR}
                  lang={lang}
                />
              </Section>

              {/* iter653 — BPP: 12-Month Personalised Prevention Program */}
              <Section icon={Dna} title={t.sections.prevention} help={null} testid="section-prevention-program">
                <BreastPreventionProgram
                  snapshot={data}
                  conditions={data?.conditions || EMPTY_ARR}
                  lang={lang}
                />
              </Section>

              {/* iter770 + 780 — Симулатори (What-If + Recovery Path). Разположени
                  СЛЕД конкретния план: пациентът първо вижда препоръчания план,
                  после може да симулира „а ако имам още едно състояние" или
                  „а ако постигна тази цел". */}
              <Section icon={Sparkles} title="Симулатори — какво ако…" help="Образователни симулатори за визуализация на въздействието." testid="section-simulators">
                <div className="space-y-4">
                  <WhatIfSimulator isDark={isDark} bg={lang !== "en"} />
                  <RecoveryPathSimulator isDark={isDark} bg={lang !== "en"} />
                </div>
              </Section>

              {/* iter1114.b — Системни модификатори (Кардио + Хормонален) */}
              <Section icon={Heart} title="Системни модификатори" help={null} testid="section-systemic-factors">
                <BhiSystemicFactorsCard isDark={isDark} />
              </Section>

              {/* ═══════════════════ 🤝 ACT ═══════════════════ */}
              <div style={ord("phase-act")}>
                <PhaseHeader emoji="🤝" title={t.phases.act} subtitle={t.phases.act_sub} anchorId="act" /></div>

              {/* iter654 — BHC: Dynamic monthly Coach */}
              <Section icon={Dna} title={t.sections.coach} help={null} testid="section-coach">
                <BreastHealthCoach lang={lang} />
              </Section>

              {/* ── Intervention Engine ────────────────────────── */}
              <Section icon={Apple} title={t.sections.interventions} help={<PlainText clinical={t.interventionsHelp} plain={t.interventionsHelpPlain} />} testid="section-interventions">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <InterventionGroup
                    icon={Apple}      color="#22C55E" title={t.nutrition}
                    items={data.interventions.nutrition} cardCls={cardCls} isDark={isDark}
                    evidenceLabels={t} testid="iv-nutrition"
                  />
                  <InterventionGroup
                    icon={Brain}      color="#6366F1" title={t.mindbody}
                    items={data.interventions.mindbody} cardCls={cardCls} isDark={isDark}
                    evidenceLabels={t} testid="iv-mindbody"
                  />
                  <InterventionGroup
                    icon={Activity}   color="#F43F5E" title={t.activity}
                    items={data.interventions.activity} cardCls={cardCls} isDark={isDark}
                    evidenceLabels={t} testid="iv-activity"
                  />
                  <InterventionGroup
                    icon={Pill}       color="#A855F7" title={t.supplements}
                    items={data.interventions.supplements} cardCls={cardCls} isDark={isDark}
                    evidenceLabels={t} testid="iv-supplements"
                  />
                </div>
              </Section>

              {/* iter1114.b — Оценка на модула: едно докосване в края на етап „Действай" */}
              <div className="my-4">
                <ModuleRatingButton moduleKey="bhi" moduleName="анализа на здравето на гърдата" isDark={isDark} />
              </div>

              {/* ═══════════════════ 🩺 SUPPORT ═══════════════════ */}
              <div style={ord("phase-support")}>
                <PhaseHeader emoji="🩺" title={t.phases.support} subtitle={t.phases.support_sub} anchorId="support" /></div>

              {/* ── Ecosystem Map (moved up per user request iter747) ─── */}
              <Section icon={BookOpen} title={t.sections.ecosystem} help={<PlainText clinical={t.ecosystemHelp} plain={t.ecosystemHelpPlain} />} testid="section-ecosystem">
                <div className={`${cardCls} p-5`}>
                  <EcosystemPathwayMap ecosystem={data.ecosystem} biology={data.biology} metabolic={data.metabolic} density={data.density} nutrition={data.signals} lang={lang} />
                </div>
              </Section>

              {/* iter933 — BCWeeklyMenuCard fully removed. There is
                  already a similar module above (BC-Friendly menu-targeting
                  themes rendered via BCSubtypePanel), so this duplicate
                  card was confusing the patient with the same header
                  appearing twice on the page. */}

              {/* iter668 — Referral widget (shows after first upload; powered by sources_used) */}
              <div style={ord("support-referral")}>
                <BhiReferralWidget />
              </div>

              {/* iter1043 — Essential-tier CTA banner (persistent, expands to Advanced) */}
              {viewTier === "essential" && (
                <div
                  data-testid="bhi-essential-cta-banner"
                  style={ord("support-cta")}
                  className={`mt-6 rounded-2xl border p-4 flex items-start gap-3 ${
                    isDark
                      ? "bg-purple-500/10 border-purple-500/30 text-purple-100"
                      : "bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 text-purple-900"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    isDark ? "bg-purple-500/20 text-purple-300" : "bg-white text-purple-600"
                  }`}>
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold">Има още 24 модула, готови за теб</div>
                    <p className="text-xs opacity-80 mt-0.5">
                      Биологични пътища · 20 близнака · симулатори · 12-месечна програма за превенция · карта на екосистемата и още.
                      Активирай Advanced режим когато искаш да видиш пълната дълбочина.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPersistentTier("advanced")}
                    data-testid="bhi-essential-cta-unlock"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 text-xs font-semibold transition"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Активирай Advanced
                  </button>
                </div>
              )}

              {/* iter740 — Save current BHI as a versioned snapshot */}
              <div className="mt-6" style={ord("support-snapshot")}>
                <BhiSnapshotSaveCard isDark={isDark} />
              </div>

              {/* iter593 — Bottom „Към инструментите" footer button премахнат по user request
                  (модулите вече са вътре в Combined Risk Profile section по-горе) */}

              {/* Bottom legal disclaimer */}
              <div className="mt-6 p-3 rounded-lg text-[11px] opacity-70 text-center"
                style={ord("support-footnote")}>
                {data.disclaimer}
              </div>
              </div>{/* /main content column (iter1032) */}
              </ViewPrefsContext.Provider>
              </BhiOrderContext.Provider>
            </div>
          )}
        </div>
        <SubpageFooter />
        {/* Cytology zoom modal */}
        <CytologyZoom zone={cytologyZone} onClose={() => setCytologyZone(null)} lang={lang} />
        {/* iter742 — "Откъде да започна" 30-day orientation modal */}
        <BhiGettingStartedModal open={gsOpen} onClose={() => setGsOpen(false)} isDark={isDark} />
        {/* iter750 — "Одит на данните" full signal audit */}
        <BhiAuditModal open={auditOpen} onClose={() => setAuditOpen(false)} isDark={isDark} />
      </div>
      </BhiReadingLevelProvider>
    );
  }

  // ─────────────────── helper components ───────────────────

  // iter994 — По искане на потребителя: всички табла свити по подразбиране
  // за да няма визуален шок при първо отваряне. Потребителят разгъва
  // ръчно чрез chevron или „Разгъни всички". Обединено с 4-стъпков
  // onboarding banner отгоре.
  const ESSENTIAL_TESTIDS = new Set([]);

  // iter1043 — Essential tier: только 5 „core" sections се рендерират;
  // всички останали са скрити. Advanced tier рендерира всичко.
  const ESSENTIAL_TIER_TESTIDS = new Set([
    "section-metabolic",
    "section-hereditary",
    "section-tissue-plan",
    "section-interventions",
    "section-ai-summary",
    "section-biology-observe",   // iter1007 — тримерният модел е основен изглед
    "section-bhi-sankey",   // iter999 — потокът на риска е ключов за разбиране
    "section-screening-plan",   // iter1014 — личният план за скрининг е основен
    "section-physiology-bridge",   // физиологичният мост е основен и в другите близнаци
    "section-systemic-factors",   // iter1114.b — методиране + надбъбречна функция
  ]);
  const ESSENTIAL_TIER_PHASES = new Set([
    // Phase headers whose subordinate sections are (partly) in ESSENTIAL_TIER
    // ("assess" holds hereditary; "observe" holds metabolic; "plan" holds tissue-plan;
    //  "act" holds interventions; "support" is fully hidden).
    "assess", "observe", "plan", "act",
  ]);

  function Section({ icon: Icon, title, help, children, testid, methodologyInfo }) {
    const orderStyle = useBhiOrderStyle(testid);
    // iter1043 — hidden in Essential mode when not in the whitelist
    if (typeof window !== "undefined") {
      try {
        const tier = localStorage.getItem("bhi_view_tier") || "essential";
        if (tier === "essential" && !ESSENTIAL_TIER_TESTIDS.has(testid)) {
          return null;
        }
      } catch { /* noop */ }
    }
    return (
      <HideableSection id={testid} label={title} style={orderStyle}>
        <BhiSection
          icon={Icon}
          title={title}
          help={help}
          testid={testid}
          methodologyInfo={methodologyInfo}
          defaultOpen={ESSENTIAL_TESTIDS.has(testid)}
        >
          {children}
        </BhiSection>
      </HideableSection>
    );
  }

  function InterventionGroup({ icon: Icon, color, title, items, cardCls, isDark, evidenceLabels, testid }) {
    const evidenceMap = {
      A: evidenceLabels.evidenceA,
      B: evidenceLabels.evidenceB,
      C: evidenceLabels.evidenceC,
    };
    return (
      <div className={`${cardCls} p-4`} data-testid={testid}>
        <div className="flex items-center gap-2 mb-3">
          <Icon className="w-5 h-5" style={{ color }} />
          <h4 className="font-bold text-sm">{title}</h4>
        </div>
        <ul className="space-y-3">
          {items.map((it, i) => {
            const ev = EVIDENCE_COLORS[it.evidence] || EVIDENCE_COLORS.C;
            return (
              <li key={i} className="text-xs" data-testid={`${testid}-item-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold leading-tight flex-1">{it.title}</div>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${
                      isDark ? ev.dark : ev.bg + " " + ev.text
                    }`}
                    title={evidenceMap[it.evidence]}
                  >
                    {it.evidence}
                  </span>
                </div>
                <div className="opacity-70 leading-snug">{it.rationale}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* iter846 — PhaseHeader extracted to BhiPhaseHeader.jsx. Local alias kept
   so existing JSX (<PhaseHeader ... />) keeps working without rename.
   iter1043 — wrapper hides phase headers whose subordinate sections are
   all filtered out in Essential tier ("understand" and "support"). */
const PhaseHeader = (props) => {
  if (typeof window !== "undefined") {
    try {
      const tier = localStorage.getItem("bhi_view_tier") || "essential";
      const hidden_in_essential = new Set(["understand", "support"]);
      if (tier === "essential" && hidden_in_essential.has(props.anchorId)) {
        return null;
      }
    } catch { /* noop */ }
  }
  return <BhiPhaseHeader {...props} />;
};

/* iter846 — FusionAuditPanel extracted to its own file. */

/* iter656 — Extracted Biology Dashboard markup so we can place it inside OBSERVE phase. */
function BiologyDashboardInline({ data, monthsAhead, setMonthsAhead, selectedZone, setSelectedZone, cardCls, isDark, lang, t }) {
  const biologyNow = monthsAhead > 0 && data.time_travel?.biology ? data.time_travel.biology : data.biology;

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <div className={`${cardCls} p-5 lg:col-span-3 relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
        <BreastAnatomyVisual
          biology={biologyNow}
          onSelect={setSelectedZone}
          selected={selectedZone}
          lang={lang}
          monthsAhead={monthsAhead}
        />
        <TimeTravelSlider
          months={monthsAhead}
          onChange={setMonthsAhead}
          timeTravel={data.time_travel}
          currentMetabolic={data.metabolic}
          lang={lang}
          isDark={isDark}
        />
      </div>
      <div className="lg:col-span-2 space-y-3">
        <ZoneDetails
          zone={selectedZone}
          biology={monthsAhead > 0 && data.time_travel?.biology ? data.time_travel.biology : data.biology}
          t={t}
          cardCls={cardCls}
          lang={lang}
          twinAttribution={data.twin_attribution}
        />
        <p className={`text-xs italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t.selectZone}</p>
      </div>
    </div>
  );
}

